import { AxiosResponse } from "axios";

import { PreferredLocale } from "../../../utils/locales";
import { AnswerSummaryState, PartInstanceSummaryResponse } from "./types";
import {
  CourseComponentType,
  IChoiceExerciseContent,
  ICourseLocalizedText,
} from "../../../components/course-author/types";

export enum AnalyticsViews {
  Graphs = "graphs",
  Users = "users",
}

export interface ITopicsChartData {
  topics: number;
  students: number | null;
  filtered?: number | null;
}

export interface IUnitAnalytics {
  lastSeen: number | null;
  teachings: number;
  time: number;
  assessments: AssessmentResult[][];
}
export enum AssessmentResult {
  PreAssessmentPassed = 0,
  PreAssessmentFailed = 1,
  PreAssessmentAborted = 2,
  PostAssessmentPassed = 3,
  PostAssessmentFailed = 4,
  PostAssessmentAborted = 5,
}

export interface IUserAnalytics extends IUnitAnalytics {
  completed: boolean;
  courseRevId: number;
  resets: number;
  unitSummaryId: string;
}

export interface IUser {
  accountId: string;
  displayName: string;
}

export interface IUsersAnalytics {
  [accountId: string]: IUserAnalytics;
}

export interface IUnitsAnalytics {
  name: string;
  level: number;
  localizedUnitName: ICourseLocalizedText[];
  analytics: { [accountId: string]: IUnitAnalytics };
  exercises: ICourseExercise[];
  questions: ICourseComponentPart[];
}

export enum UserFilter {
  MarkedInTable,
  NameMatches,
  TimeTakenBetween,
  AssessmentState,
  TookTopic,
}

export interface IUserFilter {
  name: string;
  type: UserFilter;
  labels: string[];
  enabled: boolean;
  conditions: FilterFunction[];
}

export type FilterFunction = (user: IUser) => boolean;

export enum UserStatus {
  NotStarted = "Not Started",
  InProgress = "In Progress",
  Completed = "Completed",
}

export type OnUpdateFilterLabelAndCondition = (name: string, type: UserFilter) => (index: number) => () => void;

export type TimeTakenChartFilter = (minTime: number, maxTime: number) => void;

export type AssessmentChartFilter = (status: AssessmentStatus) => (assessmentCategory: AssessmentType) => void;

export type TopicsChartFilter = (topic: number) => void;
export type SelectUnitName = (unitName: string) => () => void;

export interface IGraphFilters {
  assessmentChartLabels: AssessmentChartFilterLabels[];
  timeTakenChartFilter: TimeTakenChartFilter;
  assessmentsChartFilter: AssessmentChartFilter;
  topicsChartFilter: TopicsChartFilter;
  onSelectUnitName: SelectUnitName;
  selectedUnitName: string | null;
  fetchExerciseAnalytics: FetchPartInstanceAnalytics;
  selectedAssessmentIdentifier: string | null;
  selectedAssessmentName: string | null;
  selectedAssessmentType: UnitChartType | null;
}

export enum AssessmentStatus {
  Passed,
  NotPassed,
}

export enum AssessmentType {
  PreAssessment = "pre-assessment",
  FirstPostAssessment = "first post-assessment",
}

export enum AssessmentChartFilterLabels {
  PassedPreAssessment = "passed a pre-assessment",
  FailedPreAssessment = "failed a pre-assessment",
  PassedPostAssessment = "passed a 1st attempt of a post-assessment",
  FailedPostAssessment = "failed a 1st attempt of a post-assessment",
}

export interface IAssessmentGraphSummary {
  "Not passed": number;
  Passed: number;
  name: string;
  total: number;
}

export interface IPartIndex {
  index: string;
}

export interface ICourseComponentPart extends IPartIndex {
  content: IChoiceExerciseContent;
  name: string;
  tag: CourseComponentType;
}

export interface ICourseExercise {
  name: string;
  exercises: ICourseComponentPart[];
}

export type FetchPartInstanceAnalytics =
  | ((partPosition: string) => Promise<[string | null, AxiosResponse<PartInstanceSummaryResponse>]>)
  | null;

export interface IExerciseAnalytics {
  status: AnswerSummaryState;
  locale: PreferredLocale | null;
  answerTime: number;
  response?: number[] | null;
}

export interface IUnitInstanceAnalytics {
  [unitInstanceId: string]: {
    analytics: IExerciseAnalytics[];
  };
}
export interface IExerciseAnalyticsCommonProps {
  users: IUser[];
  usersAnalytics: IUsersAnalytics;
  selectedUsers: Set<string>;
  fetchAnalytics: FetchPartInstanceAnalytics;
  searchLocale: [PreferredLocale, PreferredLocale] | undefined;
  onSelectAssessmentName: OnSelectAssessmentFn;
}

export type OnSelectAssessmentFn = (
  selectedAssessmentName: string | null,
  selectedAssessmentIdentifier: string | null,
  selectedAssessmentType: UnitChartType | null,
  analytics: IUnitInstanceAnalytics
) => () => void;

export interface IUnitsValue {
  amount: number;
  percentage: number;
}

export type LocaleToShow = "total" | PreferredLocale;
export interface IUnitsDataBase {
  Correct: IUnitsValue;
  "Didn't Know": IUnitsValue;
  Incorrect: IUnitsValue;
  locale: LocaleToShow;
  name: string;
  totals: number;
}

export enum UnitsCategory {
  Correct = "Correct",
  DidntKnow = "Didn't Know",
  Incorrect = "Incorrect",
}

export interface IUnitsChartValue {
  amount: number | number[];
  percentage: number | number[];
  color: string;
  gradientColor: string;
  name: UnitsCategory;
}

export type IUnitsChartData = { [key in UnitsCategory]: IUnitsChartValue };

export interface IUnitsData extends IUnitsDataBase {
  answersByQuestion: Record<string, IUnitsDataBase> | undefined;
  identifier: string;
}

export enum UnitChartType {
  Instruction = "I",
  Exercises = "E",
  Language = "L",
  Questions = "Q",
}
